import React, { useState } from 'react';
import {
  Button, TextField, Box, Typography, Alert, Paper, Stack, Divider, InputAdornment, IconButton,
} from '@mui/material';
import ScienceOutlinedIcon from '@mui/icons-material/ScienceOutlined';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import api from '../api/axios';
import { useAuth } from './AuthContext';
import { useNavigate, Navigate } from 'react-router-dom';
import versionInfo from '../version.json';

const Login = () => {
  const { login, token } = useAuth();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await api.post('/auth/login', { username, password });
      login(res.data.token, res.data.role);
      // Go to the dashboard root (full load so the socket re-inits with auth).
      // Reloading /login would just render the login page again.
      window.location.href = '/';
    } catch (err) {
      setError(err.response?.data?.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  // Already signed in → don't show the login form (prevents the post-login bounce).
  if (token) return <Navigate to="/" replace />;

  return (
    <Box sx={{
      minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2,
      background: (t) => t.palette.mode === 'dark'
        ? 'radial-gradient(1200px 600px at 20% -10%, rgba(37,99,235,0.18), transparent), radial-gradient(1000px 500px at 100% 100%, rgba(34,211,238,0.12), transparent)'
        : 'radial-gradient(1200px 600px at 20% -10%, rgba(37,99,235,0.10), transparent), radial-gradient(1000px 500px at 100% 100%, rgba(8,145,178,0.08), transparent)',
    }}>
      <Paper sx={{ width: '100%', maxWidth: 420, p: { xs: 3, sm: 4 } }}>
        <Stack spacing={2.5}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Box sx={{
              width: 44, height: 44, borderRadius: 2.5, display: 'grid', placeItems: 'center',
              background: 'linear-gradient(135deg, #2563eb, #22d3ee)', color: '#fff',
            }}>
              <ScienceOutlinedIcon />
            </Box>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 800, lineHeight: 1.1 }}>AQUATRON</Typography>
              <Typography variant="caption" color="text.secondary" sx={{ letterSpacing: '0.06em' }}>
                IoT ANALYSIS PLATFORM
              </Typography>
            </Box>
          </Stack>

          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>Sign in</Typography>
            <Typography variant="body2" color="text.secondary">Access your device dashboard</Typography>
          </Box>

          {error && <Alert severity="error">{error}</Alert>}

          <form onSubmit={handleSubmit}>
            <Stack spacing={2}>
              <TextField
                label="Email or username" value={username}
                onChange={(e) => setUsername(e.target.value)}
                fullWidth required autoFocus autoComplete="username"
              />
              <TextField
                label="Password" type={showPw ? 'text' : 'password'} value={password}
                onChange={(e) => setPassword(e.target.value)}
                fullWidth required autoComplete="current-password"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowPw((s) => !s)} edge="end" size="small">
                        {showPw ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <Button type="submit" variant="contained" fullWidth size="large" disabled={loading}>
                {loading ? 'Signing in…' : 'Sign in'}
              </Button>
            </Stack>
          </form>

          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Button variant="text" size="small" onClick={() => navigate('/forgot-password')}>Forgot password?</Button>
            <Button variant="text" size="small" onClick={() => navigate('/signup')}>Create account</Button>
          </Stack>

          <Divider />
          <Stack spacing={0.5} alignItems="center">
            <Typography variant="caption" color="text.disabled" align="center">
              v{versionInfo.version} · © {new Date().getFullYear()} AQUATRON
            </Typography>
            <Typography variant="caption" color="text.secondary" align="center">
              Powered by{' '}
              <Typography
                component="a" variant="caption"
                href="https://www.wirelessmind.in" target="_blank" rel="noopener noreferrer"
                sx={{ color: 'primary.main', fontWeight: 700, textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
              >
                Wirelessmind
              </Typography>
            </Typography>
          </Stack>
        </Stack>
      </Paper>
    </Box>
  );
};

export default Login;
